import type { CostBenefitEstimate } from "../../../../../../api/kehrblatt/cost-benefit"

export type CostBenefitNumbersCard = {
  key: string
  label: string
  value: number | string | null
  tooltipContent?: string | null
}

const numberFormatter = new Intl.NumberFormat("de-DE")

const UNKNOWN_VALUE = "Nicht belastbar schätzbar"
const ERROR_VALUE = "Fehler"
const FILTER_SCOPE_HINT =
  "Diese Schätzung bezieht sich auf die gesamte aktuelle Auswahl, nicht nur auf gefilterte Tabellenzeilen."
const ERROR_NO_FALLBACK_HINT = "Eine verlässliche Schätzung ist derzeit nicht möglich."

const formatMinutes = (value: number | null | undefined) => {
  if (value === null || value === undefined) return UNKNOWN_VALUE

  return `${numberFormatter.format(Math.max(0, Math.round(value)))} Min.`
}

const formatCredits = (value: number | null | undefined) => {
  if (value === null || value === undefined) return UNKNOWN_VALUE

  return `${numberFormatter.format(Math.max(0, Math.round(value)))} Credits`
}

const linesToTooltip = (lines: string[]) => lines.filter(Boolean).join("<br>")

const uniqueLines = (lines: string[]) => Array.from(new Set(lines.filter(Boolean)))

const baseTooltipLines = (estimate: CostBenefitEstimate | null | undefined, fallback: string) => {
  const warnings = estimate?.warnings ?? []

  return uniqueLines([fallback, FILTER_SCOPE_HINT, ...warnings])
}

const costBreakdownLines = (estimate: CostBenefitEstimate | null | undefined) =>
  (estimate?.cost_breakdown ?? []).map((item) => {
    const count = numberFormatter.format(item.count)
    const credits = numberFormatter.format(item.credits)

    return `${item.label}: ${count} Position(en), ${credits} Credits`
  })

const buildErrorCards = (error: string): CostBenefitNumbersCard[] => {
  const tooltipContent = linesToTooltip(
    uniqueLines([error, ERROR_NO_FALLBACK_HINT, FILTER_SCOPE_HINT]),
  )

  return [
    {
      key: "time-saved",
      label: "Zeitersparnis",
      value: ERROR_VALUE,
      tooltipContent,
    },
    {
      key: "remaining-effort",
      label: "Geschätzter Aufwand",
      value: ERROR_VALUE,
      tooltipContent,
    },
    {
      key: "costs",
      label: "Kosten",
      value: ERROR_VALUE,
      tooltipContent,
    },
  ]
}

export const buildCostBenefitNumberCards = ({
  estimate,
  loading = false,
  error = null,
}: {
  estimate?: CostBenefitEstimate | null
  loading?: boolean
  error?: string | null
}): CostBenefitNumbersCard[] => {
  if (error && !loading) return buildErrorCards(error)

  const pendingValue = loading ? "wird berechnet" : null

  return [
    {
      key: "time-saved",
      label: "Zeitersparnis",
      value: pendingValue ?? formatMinutes(estimate?.time_saved_minutes),
      tooltipContent: linesToTooltip(
        baseTooltipLines(
          estimate,
          estimate?.time_saved_minutes === null || estimate?.time_saved_minutes === undefined
            ? "Für diese Aktion gibt es keinen belastbaren manuellen Vergleich."
            : "Geschätzte eingesparte manuelle Arbeit für die gesamte Auswahl.",
        ),
      ),
    },
    {
      key: "remaining-effort",
      label: "Geschätzter Aufwand",
      value: pendingValue ?? formatMinutes(estimate?.remaining_effort_minutes),
      tooltipContent: linesToTooltip(
        baseTooltipLines(
          estimate,
          estimate?.remaining_effort_minutes === null ||
            estimate?.remaining_effort_minutes === undefined
            ? "Der Aufwand hängt von den nächsten Entscheidungen ab und lässt sich noch nicht schätzen."
            : "Geschätzte Arbeit, die Sie anschließend noch selbst erledigen müssen.",
        ),
      ),
    },
    {
      key: "costs",
      label: "Kosten",
      value: pendingValue ?? formatCredits(estimate?.total_credits),
      tooltipContent: linesToTooltip([
        ...baseTooltipLines(
          estimate,
          estimate?.total_credits === null || estimate?.total_credits === undefined
            ? "Die Kosten können für diese Aktion noch nicht belastbar berechnet werden."
            : "Geschätzte Kosten nach Ihrem aktuellen Tarif.",
        ),
        ...(estimate?.assumptions ?? []),
        ...costBreakdownLines(estimate),
      ]),
    },
  ]
}
